import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatarCnpj, ROTULO_REGIME } from '@via-tax/contracts'
import { Aviso, Botao, Entrada, EstadoVazio, Selo } from '@via-tax/ui'
import { useEffect, useState } from 'react'
import { mensagemDeErro, useTRPC } from '../trpc'

function useAdiado<T>(valor: T, ms: number): T {
  const [v, setV] = useState(valor)
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms)
    return () => clearTimeout(t)
  }, [valor, ms])
  return v
}

export function HabilitarEmpresas() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [termo, setTermo] = useState('')
  const busca = useAdiado(termo.trim(), 300)
  const resultados = useQuery({ ...trpc.empresa.buscarNoProcessos.queryOptions({ termo: busca }), enabled: busca.length >= 3 })
  const [habilitadas, setHabilitadas] = useState<string[]>([])

  const habilitar = useMutation(
    trpc.empresa.habilitar.mutationOptions({
      onSuccess: (_r, vars) => {
        setHabilitadas((h) => [...h, vars.processosId])
        void qc.invalidateQueries({ queryKey: trpc.sessao.empresasDisponiveis.queryKey() })
        void qc.invalidateQueries({ queryKey: trpc.empresa.buscarNoProcessos.queryKey() })
      },
    }),
  )

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Habilitar empresas</h1>
        <p className="mt-1 max-w-prose text-sm text-tinta-2">
          Todo cliente do Processos chega ao Via Tax, mas só aparece no seletor depois de habilitado. Matriz e filiais
          (mesma raiz de CNPJ) formam uma empresa só: habilitar vale para todas as unidades.
        </p>
      </header>

      <div className="max-w-xl">
        <Entrada
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          placeholder="Razão social, nome fantasia ou CNPJ (mín. 3 caracteres)"
          autoFocus
        />
      </div>

      {habilitar.isError && <Aviso>{mensagemDeErro(habilitar.error)}</Aviso>}

      {busca.length < 3 ? (
        <EstadoVazio titulo="Busque no cadastro do Processos">
          A busca olha o espelho local, atualizado por webhook e conferido a cada 15 minutos. Se um cliente recém-criado não
          aparecer, use "Sincronizar agora" em Integrações.
        </EstadoVazio>
      ) : resultados.isPending ? (
        <p className="text-sm text-tinta-3">Buscando…</p>
      ) : resultados.isError ? (
        <Aviso>{mensagemDeErro(resultados.error)}</Aviso>
      ) : resultados.data.length === 0 && habilitadas.length === 0 ? (
        <EstadoVazio titulo="Nenhum cliente não habilitado com esse termo">
          Pode ser que ele já esteja habilitado (confira o seletor), que seja pessoa física — o Via Tax só simula CNPJ — ou
          que ainda não tenha sincronizado.
        </EstadoVazio>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-linha bg-painel">
          <table className="w-full min-w-[640px] text-left text-[13px]">
            <thead className="border-b border-linha text-[11px] uppercase tracking-[0.06em] text-tinta-3">
              <tr>
                <th className="px-4 py-2.5 font-medium">Empresa</th>
                <th className="px-4 py-2.5 font-medium">CNPJ</th>
                <th className="px-4 py-2.5 font-medium">Regime (Processos)</th>
                <th className="px-4 py-2.5 font-medium">Situação</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-linha">
              {resultados.data.map((r) => (
                <tr key={r.processosId}>
                  <td className="px-4 py-2.5">
                    <p className="font-medium">{r.nomeFantasia || r.razaoSocial}</p>
                    <p className="text-xs text-tinta-3">
                      {r.nomeFantasia ? `${r.razaoSocial} · ` : ''}
                      {r.temMatriz ? 'matriz' : 'sem matriz no Processos'}
                      {r.filiais > 0 && ` + ${r.filiais} filia${r.filiais > 1 ? 'is' : 'l'}`}
                    </p>
                  </td>
                  <td className="num px-4 py-2.5 whitespace-nowrap">{formatarCnpj(r.cnpj)}</td>
                  <td className="px-4 py-2.5">{r.regimeTributario ? ROTULO_REGIME[r.regimeTributario] : <span className="text-tinta-3">—</span>}</td>
                  <td className="px-4 py-2.5">
                    {r.situacaoCadastral === 'inactive' ? <Selo tom="alerta">Inativa</Selo> : <Selo>{r.situacaoCadastral ?? '—'}</Selo>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Botao
                      variante="primario"
                      disabled={habilitar.isPending}
                      onClick={() => r.processosId && habilitar.mutate({ processosId: r.processosId })}
                    >
                      Habilitar
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {habilitadas.length > 0 && (
            <p className="border-t border-linha px-4 py-2.5 text-xs text-ok">
              {habilitadas.length} empresa(s) habilitada(s) nesta busca — já disponíveis no seletor.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
